import type { Conversation } from '@/features/conversation/models/conversation';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversations } from '../../queries/conversation.queries';
import ConversationItem from './conversation-item';

interface ConversationListProps {
  searchQuery?: string;
  conversationId?: string;
  onSelect?: (conv: Conversation) => void;
}

export default function ConversationList({ searchQuery = '', conversationId, onSelect }: ConversationListProps) {
  const { t } = useTranslation();

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, error } = useConversations();

  const conversations = useMemo((): Conversation[] => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.conversations).sort((a, b) => dayjs(b.updatedAt).diff(dayjs(a.updatedAt)));
  }, [data?.pages]);

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: conversations.length + (hasNextPage ? 1 : 0),
    estimateSize: () => 72,
    getScrollElement: () => parentRef.current,
    overscan: 8,
    gap: 2,
    getItemKey: (index) => conversations[index]?.conversationId || index
  });

  const handleConvClick = (conversation: Conversation) => {
    if (conversation.conversationId === conversationId) return;
    onSelect?.(conversation);
  };

  // Scroll handler for loading more at end
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        ticking = false;
        const { scrollTop, scrollHeight, clientHeight } = el;
        if (
          scrollTop + clientHeight >= scrollHeight - 50 && // threshold
          virtualizer.getVirtualItems().length === conversations.length &&
          hasNextPage &&
          !isFetching
        ) {
          fetchNextPage();
        }
      });
      ticking = true;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [virtualizer, conversations.length, hasNextPage, isFetching, fetchNextPage]);

  if (error) {
    return (
      <Box className='mex:flex mex:items-center mex:justify-center mex:p-8'>
        <Typography color='error' variant='body2'>
          Có lỗi xảy ra khi tải danh sách cuộc trò chuyện
        </Typography>
      </Box>
    );
  }

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  if (isLoading && conversations.length === 0) {
    return (
      <Box className='mex:flex mex:h-full mex:items-center mex:justify-center mex:p-8'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className='mex:h-full'>
      <Box ref={parentRef} className='mex:h-full mex:overflow-auto mex:px-2' style={{ scrollbarWidth: 'thin' }}>
        <Box
          style={{
            height: `${totalSize}px`,
            width: '100%',
            position: 'relative'
          }}
        >
          {items.map((item) => {
            if (item.index >= conversations.length) {
              // Loading indicator at end
              return (
                <Box
                  key='loading-end'
                  ref={virtualizer.measureElement}
                  data-index={item.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: 80,
                    transform: `translateY(${item.start}px)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              );
            }

            const conversation = conversations[item.index];
            return (
              <Box
                key={conversation.conversationId}
                ref={virtualizer.measureElement}
                data-index={item.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                  transform: `translateY(${item.start}px)`
                }}
                className='mex:mb-1'
              >
                <ConversationItem
                  conversation={conversation}
                  isSelected={conversationId === conversation.conversationId}
                  onClick={() => handleConvClick(conversation)}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      {conversations.length === 0 && !isLoading && (
        <Box className='mex:absolute mex:inset-0 mex:flex mex:items-center mex:justify-center mex:p-8'>
          <Typography color='text.secondary' variant='body2'>
            {searchQuery ? t('conversations.empty.searchEmpty') : t('conversations.empty.noConversations')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
